import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, X, Plus, Minus, Trash2 } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const Cart = () => {
  const {
    cart,
    isCartOpen,
    setIsCartOpen,
    updateQuantity,
    removeFromCart,
    getCartTotal,
    DELIVERY_FEE,
    getOrderTotal,
  } = useCart();

  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <>
      {/* Cart Drawer Overlay */}
      {isCartOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 transition-opacity"
          onClick={() => setIsCartOpen(false)}
        />
      )}

      {/* Cart Drawer */}
      <div
        className={`fixed right-0 top-0 z-50 h-full w-full max-w-md transform bg-white shadow-2xl transition-transform duration-300 ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-charcoal/10 p-6">
            <h2 className="text-2xl font-bold text-charcoal">Your Cart</h2>
            <button
              onClick={() => setIsCartOpen(false)}
              className="rounded-full p-2 hover:bg-surface transition"
              aria-label="Close cart"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {cart.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <ShoppingCart className="h-16 w-16 text-charcoal/20 mb-4" />
                <p className="text-ink-2">Your cart is empty</p>
                <p className="text-sm text-ink-3 mt-2">Add some products to get started!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div
                    key={`${item.id}-${item.source}`}
                    className="flex gap-4 rounded-2xl border border-charcoal/10 bg-surface p-4"
                  >
                    {/* Product Image */}
                    <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-surface-alt">
                      {item.imgPath ? (
                        <img
                          src={item.imgPath}
                          alt={item.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-charcoal/30">
                          No image
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex flex-1 flex-col justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-charcoal leading-snug">
                          {item.name}
                        </h3>
                        <p className="text-xs text-ink-3 mt-1">
                          {item.source === 'lithophane' ? 'Lithophane Lamp' : item.category}
                        </p>
                        <p className="text-sm font-bold text-primary mt-1">
                          Rs. {item.price.toLocaleString('en-LK')}
                        </p>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.source, item.quantity - 1)}
                            className="flex h-7 w-7 items-center justify-center rounded-full border border-charcoal/20 hover:bg-surface-alt transition"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.source, item.quantity + 1)}
                            className="flex h-7 w-7 items-center justify-center rounded-full border border-charcoal/20 hover:bg-surface-alt transition"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.id, item.source)}
                          className="text-rose-500 hover:text-rose-600 transition"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with Total and Checkout */}
          {cart.length > 0 && (
            <div className="border-t border-charcoal/10 p-6 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-2">Subtotal</span>
                  <span className="font-semibold">Rs. {getCartTotal().toLocaleString('en-LK')}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-2">Delivery</span>
                  <span className="font-semibold">Rs. {DELIVERY_FEE.toLocaleString('en-LK')}</span>
                </div>
                <div className="flex items-center justify-between border-t border-charcoal/10 pt-2">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-lg font-bold text-primary">
                    Rs. {getOrderTotal().toLocaleString('en-LK')}
                  </span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full rounded-full bg-primary py-4 text-center font-semibold text-white transition hover:bg-primary/90"
              >
                Proceed to Checkout
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
